import { Button, Card, Layout, Modal, Select, Space, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Locale } from "../../utils";
import {
  handleMutationError,
  showNotificationWithIcon,
} from "../../utils/helpers/helpers";
import { getOne, create, update } from "../../utils/http/dataProvider";
import { publishCustomPage } from "../../utils/http/customPageDataProvider";
import { getUserInfo } from "../../utils/redux/reducers/User";
import {
  setCurrentPage,
  resetCurrentPage,
} from "../../utils/redux/actions/CustomPage";
import { getCurrentPage } from "../../utils/redux/reducers/CustomPage";
import { BreadCrumb } from "../../components/main/LayoutComponents";
import LanguageSwitcher from "./LanguageSwitcher";
import BlockPalette from "./BlockPalette";
import BlockList from "./BlockList";
import BlockEditor from "./BlockEditor";
import PagePreview from "./PagePreview";
import { validateBlockLimits, BLOCK_LIMITS } from "./blockConfigs";

import "./PageEditor.scss";

const { Content, Sider } = Layout;
const { Option } = Select;
const i18n = Locale.getInstance();

const PageEditor = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector((state) => getUserInfo(state));
  const queryClient = useQueryClient();
  const currentPage = useSelector(getCurrentPage);

  const [loading, setLoading] = useState(false);
  const [pageType, setPageType] = useState("home");
  const [language, setLanguage] = useState("ug");
  const [isNewPage, setIsNewPage] = useState(false);

  const pageId = location.pathname.includes("/create")
    ? null
    : location.pathname.split("/").pop();

  useEffect(() => {
    if (location.pathname.includes("/create")) {
      setIsNewPage(true);
      dispatch(
        setCurrentPage({
          id: null,
          page_type: pageType,
          language: language,
          status: "draft",
          layout_data: [],
          version: 1,
          published_at: null,
        })
      );
    }
  }, [location.pathname, dispatch, pageType, language]);

  useEffect(() => {
    if (currentPage && isNewPage) {
      dispatch(
        setCurrentPage({
          ...currentPage,
          page_type: pageType,
          language: language,
        })
      );
    }
  }, [pageType, language, isNewPage, dispatch]);

  const { data, isLoading } = useQuery(
    ["custom-page", pageId, user.token],
    () => getOne("custom-pages", { id: pageId, token: user.token }),
    {
      enabled: !!pageId && !isNewPage,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data) {
          dispatch(setCurrentPage(data));
          setPageType(data.page_type);
          setLanguage(data.language);
        }
      },
    }
  );

  const createMutation = useMutation(create, {
    onSuccess: () => {
      queryClient.invalidateQueries("custom-pages");
      showNotificationWithIcon(i18n.t("customPage.saveSuccess"), "success");
      setLoading(false);
      history.push("/main/custom-pages");
    },
    onError: (error) => {
      handleMutationError(error);
      setLoading(false);
    },
  });

  const updateMutation = useMutation(update, {
    onSuccess: () => {
      queryClient.invalidateQueries("custom-pages");
      queryClient.invalidateQueries(["custom-page", pageId]);
      showNotificationWithIcon(i18n.t("customPage.saveSuccess"), "success");
      setLoading(false);
    },
    onError: (error) => {
      handleMutationError(error);
      setLoading(false);
    },
  });

  const publishMutation = useMutation(
    (id) => publishCustomPage(id, user.token),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("custom-pages");
        queryClient.invalidateQueries(["custom-page", pageId]);
        showNotificationWithIcon(i18n.t("customPage.publishSuccess"), "success");
        setLoading(false);
        history.push("/main/custom-pages");
      },
      onError: (error) => {
        handleMutationError(error);
        setLoading(false);
      },
    }
  );

  const handleSaveDraft = () => {
    if (!currentPage) return;

    const blocks = currentPage.layout_data || [];
    if (blocks.length > BLOCK_LIMITS.MAX_BLOCKS_PER_PAGE) {
      showNotificationWithIcon(i18n.t("customPage.errorMaxBlocks"), "error");
      return;
    }

    setLoading(true);
    const pageData = {
      page_type: currentPage.page_type || pageType,
      language: currentPage.language || language,
      layout_data: blocks,
    };

    if (isNewPage || !currentPage.id) {
      createMutation.mutate({
        resource: "custom-pages",
        params: {
          data: pageData,
          token: user.token,
        },
      });
    } else {
      updateMutation.mutate({
        resource: "custom-pages",
        params: {
          id: currentPage.id,
          data: pageData,
          token: user.token,
        },
      });
    }
  };

  const handlePublish = () => {
    if (!currentPage || !currentPage.id) {
      showNotificationWithIcon(i18n.t("customPage.saveBeforePublish"), "warning");
      return;
    }

    const blocks = currentPage.layout_data || [];
    if (blocks.length > BLOCK_LIMITS.MAX_BLOCKS_PER_PAGE) {
      showNotificationWithIcon(i18n.t("customPage.errorMaxBlocks"), "error");
      return;
    }

    Modal.confirm({
      title: i18n.t("customPage.publishConfirm"),
      content: i18n.t("customPage.publishConfirmMessage"),
      onOk: () => {
        setLoading(true);
        publishMutation.mutate(currentPage.id);
      },
      okText: i18n.t("base.modal.okText"),
      cancelText: i18n.t("base.modal.cancelText"),
    });
  };

  useEffect(() => {
    return () => {
      dispatch(resetCurrentPage());
    };
  }, [dispatch]);

  const breadCrumbData = [
    {
      href: "/main/custom-pages",
      title: i18n.t("customPage.title"),
    },
    {
      title: isNewPage ? i18n.t("customPage.create") : i18n.t("customPage.edit"),
    },
  ];

  return (
    <>
      <BreadCrumb data={breadCrumbData} />
      <Spin spinning={isLoading || loading}>
        <Layout className="page-editor-layout">
          <div className="page-editor-header">
            <Space>
              {isNewPage && (
                <>
                  <Select
                    value={pageType}
                    onChange={setPageType}
                    style={{ width: 120 }}
                  >
                    <Option value="home">{i18n.t("customPage.pageTypeHome")}</Option>
                    <Option value="user">{i18n.t("customPage.pageTypeUser")}</Option>
                    <Option value="video-list">{i18n.t("customPage.pageTypeVideoList")}</Option>
                  </Select>
                  <Select
                    value={language}
                    onChange={setLanguage}
                    style={{ width: 120 }}
                  >
                    <Option value="ug">{i18n.t("customPage.languageUyghur")}</Option>
                    <Option value="zh">{i18n.t("customPage.languageChinese")}</Option>
                  </Select>
                </>
              )}
              <LanguageSwitcher />
              <Button onClick={handleSaveDraft} type="primary">
                {i18n.t("customPage.saveDraft")}
              </Button>
              {!isNewPage && currentPage?.id && (
                <Button onClick={handlePublish} type="primary" danger>
                  {i18n.t("customPage.publish")}
                </Button>
              )}
              <Button onClick={() => history.goBack()}>
                {i18n.t("common.cancel")}
              </Button>
            </Space>
          </div>
          <Layout>
            <Sider width={300} className="page-editor-sider-left">
              <BlockPalette />
            </Sider>
            <Content className="page-editor-content">
              <Layout>
                <Sider width={200} className="page-editor-sider-middle">
                  <BlockList />
                </Sider>
                <Content className="page-editor-preview">
                  <PagePreview />
                </Content>
                <Sider width={690} className="page-editor-sider-right">
                  <BlockEditor />
                </Sider>
              </Layout>
            </Content>
          </Layout>
        </Layout>
      </Spin>
    </>
  );
};

export default PageEditor;

